// Tidsbokning Phase C — RescheduleLeagueMatchDialog
//
// Sibling to MoveBookingDialog, but specialised for league_matches. Opens
// from MatchDetailModal ("Flytta match" button) or from Phase D drag-drop
// on a Seriespel block.
//
// Form fields (per design doc §4.B):
//   - Court selector (defaults to current court)
//   - Date picker (defaults to effective scheduled_date)
//   - Time picker, 15-min snap (D5)
//   - Duration readonly, from league.match_duration_minutes or booking's
//     duration_minutes (D6)
//   - Reason (optional textarea)
//   - Cancel / Confirm
//
// On Confirm:
//   1. supabase.rpc('admin_reschedule_league_match', {...}) — single atomic
//      mutation, all DB-side propagation handled by the RPC.
//   2. Post-RPC fire-and-forget (D1/D2/D3):
//      a. System chat row via sendLeagueMatchMessage
//      b. If pre_reschedule_lock_notif_sent: invoke enqueue_match_lock_notifications
//         to immediately re-fan (next cron tick would catch it anyway, but
//         doing it now closes the gap). Re-fan goes out with "(uppdaterad)"
//         subject thanks to the is_resend flag (Tidsbokning Phase C trio:
//         migration + enqueue + send-worker).

import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fromStockholmWallClock, addMinutes } from "@/lib/timezone";
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";

export interface MatchRescheduleSource {
  /** league_matches.id */
  matchId: string;
  /** Currently linked booking (if any). Affects which path the RPC takes. */
  bookingId: string | null;
  /** Current court (defaults the form). */
  currentCourtId: string | null;
  /** Current effective date (rescheduled_* or scheduled_*). */
  currentDate: string | null;
  /** Current effective time (HH:MM:SS). */
  currentTime: string | null;
  /** Duration shown read-only — typically league.match_duration_minutes. */
  durationMinutes: number;
  /** Human-friendly league/match label for the dialog header. */
  label: string;
  /** Home/away teams + court name for the success-toast wording. */
  homeTeamLabel?: string | null;
  awayTeamLabel?: string | null;
}

interface RescheduleLeagueMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: MatchRescheduleSource;
  /** Available courts in the same hall/org. */
  courts: Array<{ id: string; name: string }>;
  /** Pre-fill TILL fields from a drag-drop (Phase D). Optional. */
  initialTarget?: { courtId?: string; date?: string; time?: string } | null;
  /** Called after a successful reschedule so the parent can refresh data. */
  onRescheduled: () => void;
  /** Admin uid for the RPC — typically auth.uid() at session level. */
  adminId?: string;
}

const TIME_STEP_MIN = 15; // D5 snap

function snapTimeTo(value: string, stepMin: number): string {
  // value = "HH:MM" → snap to nearest stepMin
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const total = h * 60 + m;
  const snapped = Math.round(total / stepMin) * stepMin;
  const hh = Math.floor(snapped / 60) % 24;
  const mm = snapped % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export function RescheduleLeagueMatchDialog({
  open,
  onOpenChange,
  source,
  courts,
  initialTarget,
  onRescheduled,
  adminId,
}: RescheduleLeagueMatchDialogProps) {
  // Default values when dialog opens
  const defaultCourt = initialTarget?.courtId ?? source.currentCourtId ?? courts[0]?.id ?? "";
  const defaultDate = initialTarget?.date ?? source.currentDate ?? "";
  const defaultTime = (initialTarget?.time ?? source.currentTime ?? "").slice(0, 5);

  const [courtId, setCourtId] = useState<string>(defaultCourt);
  const [dateStr, setDateStr] = useState<string>(defaultDate);
  const [timeStr, setTimeStr] = useState<string>(defaultTime);
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Re-default when dialog re-opens for a different match
  useEffect(() => {
    if (open) {
      setCourtId(defaultCourt);
      setDateStr(defaultDate);
      setTimeStr(defaultTime);
      setReason("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source.matchId]);

  const hasChanged = useMemo(() => {
    return courtId !== source.currentCourtId
      || dateStr !== source.currentDate
      || timeStr.slice(0, 5) !== (source.currentTime ?? "").slice(0, 5);
  }, [courtId, dateStr, timeStr, source]);

  const newStart = useMemo(() => {
    if (!dateStr || !timeStr) return null;
    return fromStockholmWallClock(dateStr, timeStr);
  }, [dateStr, timeStr]);

  const newEnd = useMemo(() => {
    if (!newStart) return null;
    return addMinutes(newStart, source.durationMinutes);
  }, [newStart, source.durationMinutes]);

  const handleConfirm = async () => {
    if (!courtId || !dateStr || !timeStr) {
      toast.error("Bana, datum och tid krävs");
      return;
    }
    const snappedTime = snapTimeTo(timeStr, TIME_STEP_MIN);
    setSubmitting(true);
    try {
      // 1. RPC — atomic admin reschedule (move_booking inline for linked, direct UPDATE for unlinked)
      const { data: rpcData, error: rpcErr } = await supabase.rpc("admin_reschedule_league_match" as any, {
        _match_id: source.matchId,
        _new_date: dateStr,
        _new_time: snappedTime,
        _new_court_id: courtId,
        _admin_id: adminId ?? null,
        _reason: reason || null,
      });
      if (rpcErr) {
        const msg = rpcErr.message ?? String(rpcErr);
        toast.error(msg.includes("bokad") ? "Banan är redan bokad denna tid" : msg);
        return;
      }

      // 2. Post-RPC fire-and-forget (D1 follow-up email + D3 re-fan)
      try {
        const courtName = courts.find((c) => c.id === courtId)?.name ?? "?";
        const chatMsg = `Match flyttad till ${dateStr} ${snappedTime} på ${courtName}${reason ? `: ${reason}` : ""}`;
        await sendLeagueMatchMessage(supabase, {
          matchId: source.matchId,
          senderUserId: adminId ?? null,
          body: chatMsg,
          isSystem: true,
          data: {
            type: "admin_reschedule",
            kind: "moved",
            new_date: dateStr,
            new_time: snappedTime,
            new_court_id: courtId,
            reason: reason || null,
          },
        });
      } catch (chatErr) {
        // Non-fatal — log + continue. The reschedule itself succeeded.
        console.warn("[reschedule] system chat failed:", chatErr);
      }

      // 3. D2 + D3: if pre_reschedule_lock_notif_sent, trigger immediate re-fan
      // so the (uppdaterad) email goes out within this minute instead of waiting
      // up to 15 min for the next enqueue cron tick.
      const result = rpcData as { pre_reschedule_lock_notif_sent?: boolean } | null;
      if (result?.pre_reschedule_lock_notif_sent) {
        try {
          await supabase.rpc("enqueue_match_lock_notifications" as any);
        } catch (enqueueErr) {
          // Non-fatal — the */15 cron will catch it.
          console.warn("[reschedule] immediate re-fan failed:", enqueueErr);
        }
      }

      toast.success("Match flyttad");
      onRescheduled();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Flytta match</DialogTitle>
          <DialogDescription>{source.label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* FROM */}
          <div className="bg-muted/40 rounded p-2 text-xs">
            <div className="text-muted-foreground mb-1">Från</div>
            <div className="font-medium">
              {source.currentDate
                ? format(new Date(`${source.currentDate}T00:00:00`), "EEEE d MMM yyyy", { locale: sv })
                : "—"}
              {" kl "}
              {source.currentTime?.slice(0, 5) ?? "—"}
            </div>
            <div className="text-muted-foreground">
              {courts.find((c) => c.id === source.currentCourtId)?.name ?? "—"}
            </div>
          </div>

          <div className="flex items-center justify-center text-muted-foreground">
            <ArrowRight className="w-4 h-4" />
          </div>

          {/* TO */}
          <div className="space-y-2">
            <Label htmlFor="rsl-court">Bana</Label>
            <Select value={courtId} onValueChange={setCourtId}>
              <SelectTrigger id="rsl-court"><SelectValue placeholder="Välj bana" /></SelectTrigger>
              <SelectContent>
                {courts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="rsl-date">Datum</Label>
                <Input id="rsl-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="rsl-time">Tid (15-min snap)</Label>
                <Input
                  id="rsl-time"
                  type="time"
                  step={TIME_STEP_MIN * 60}
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  onBlur={(e) => setTimeStr(snapTimeTo(e.target.value, TIME_STEP_MIN))}
                />
              </div>
            </div>

            <div>
              <Label>Längd</Label>
              <div className="text-sm text-muted-foreground">{source.durationMinutes} min (från liga-inställning)</div>
            </div>

            <div>
              <Label htmlFor="rsl-reason">Anledning (valfri)</Label>
              <Textarea
                id="rsl-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. lagledare hörde av sig"
                rows={2}
              />
            </div>

            {newStart && newEnd && (
              <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                Ny match-tid: {format(newStart, "yyyy-MM-dd HH:mm")} – {format(newEnd, "HH:mm")} (UTC)
              </div>
            )}

            {!hasChanged && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <AlertCircle className="w-3.5 h-3.5" />
                Ingen ändring jämfört med nuvarande tid.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={submitting || !hasChanged || !courtId || !dateStr || !timeStr}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Bekräfta flytt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
